/**
 * Sample Library Browser
 * Built-in loops, one-shots, and sound effects browser
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import {
  Library,
  Search,
  Play,
  Pause,
  Heart,
  Download,
  X,
  Loader2,
  ChevronRight,
  Music2,
  Volume2,
  Clock,
  Tag,
  Filter,
  Star,
  Package,
  Globe,
} from 'lucide-react';
import { toast } from 'sonner';
import { FreesoundBrowser } from './FreesoundBrowser';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons
const CATEGORY_ICONS = {
  drums: '🥁',
  bass: '🎸',
  synths: '🎹',
  loops: '🔄',
  fx: '✨',
  vocals: '🎤',
  instruments: '🎻',
  world: '🌍',
};

// Sample card component
const SampleCard = ({ sample, isPlaying, onPlay, onStop, onDragStart, onAddToFavorites, isFavorite }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className={`group p-3 rounded-lg border transition-all cursor-pointer ${
      isPlaying
        ? 'bg-emerald-500/20 border-emerald-500/50'
        : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-500'
    }`}
    draggable
    onDragStart={(e) => onDragStart?.(e, sample)}
    onClick={() => isPlaying ? onStop() : onPlay(sample)}
  >
    <div className="flex items-start justify-between mb-2">
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-white truncate">{sample.name}</h4>
        <p className="text-xs text-zinc-500 capitalize">{sample.subcategory?.replace('-', ' ')}</p>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => { e.stopPropagation(); onAddToFavorites?.(sample); }}
          className={`p-1 rounded ${isFavorite ? 'text-red-400' : 'text-zinc-400 hover:text-red-400'}`}
        >
          <Heart className={`w-3 h-3 ${isFavorite ? 'fill-current' : ''}`} />
        </button>
      </div>
    </div>
    
    <div className="flex items-center justify-between text-xs">
      <div className="flex items-center gap-2 text-zinc-500">
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {sample.duration?.toFixed(1)}s
        </span>
        {sample.bpm && (
          <span className="px-1.5 py-0.5 bg-zinc-700 rounded text-zinc-400">
            {sample.bpm} BPM
          </span>
        )}
        {sample.key && (
          <span className="px-1.5 py-0.5 bg-zinc-700 rounded text-zinc-400">
            {sample.key}
          </span>
        )}
      </div>
      {sample.premium && (
        <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded text-[10px] font-medium">
          PRO
        </span>
      )}
    </div>
    
    {/* Play indicator */}
    {isPlaying && (
      <div className="mt-2 h-1 bg-zinc-700 rounded-full overflow-hidden">
        <motion.div
          className="h-full bg-emerald-500"
          initial={{ width: 0 }}
          animate={{ width: '100%' }}
          transition={{ duration: sample.duration || 2, ease: 'linear' }}
        />
      </div>
    )}
  </motion.div>
);

export const SampleLibraryBrowser = ({
  isOpen,
  onClose,
  onInsertSample, // Callback to insert sample into track
  audioContext,
}) => {
  // Tab state
  const [activeTab, setActiveTab] = useState('builtin'); // 'builtin' | 'freesound'
  
  // State
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [samples, setSamples] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [playingSampleId, setPlayingSampleId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [bpmFilter, setBpmFilter] = useState({ min: '', max: '' });
  const [keyFilter, setKeyFilter] = useState('');
  const [showFavorites, setShowFavorites] = useState(false);
  
  const audioRef = useRef(null);
  const oscillatorRef = useRef(null);

  // Load categories on mount
  useEffect(() => {
    if (isOpen) {
      loadCategories();
    }
  }, [isOpen]);

  // Load samples when category changes
  useEffect(() => {
    if (selectedCategory) {
      loadSamples();
    }
  }, [selectedCategory, selectedSubcategory, searchQuery, showFavorites]);

  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/samples/categories`);
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  const loadSamples = async () => {
    setIsLoading(true);
    try {
      let url;
      
      if (showFavorites) {
        url = `${API_URL}/api/samples/favorites/list`;
      } else if (searchQuery) {
        url = `${API_URL}/api/samples/search?query=${encodeURIComponent(searchQuery)}`;
        if (selectedCategory) url += `&category=${selectedCategory}`;
      } else {
        url = `${API_URL}/api/samples/browse?category=${selectedCategory}`;
        if (selectedSubcategory) url += `&subcategory=${selectedSubcategory}`;
      }
      
      const response = await fetch(url);
      const data = await response.json();
      setSamples(data.samples || []);
    } catch (err) {
      console.error('Error loading samples:', err);
      toast.error('Failed to load samples');
    } finally {
      setIsLoading(false);
    }
  };

  // Play sample preview
  const playSample = useCallback(async (sample) => {
    setPlayingSampleId(sample.id);
    
    // Stop any existing playback
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (oscillatorRef.current) {
      try { oscillatorRef.current.stop(); } catch (e) {}
    }
    
    try {
      // Create audio element to play the sample from backend
      const audio = new Audio(`${API_URL}/api/samples/audio/${sample.id}`);
      audio.volume = 0.7;
      audioRef.current = audio;
      
      audio.onended = () => {
        setPlayingSampleId(null);
        audioRef.current = null;
      };
      
      audio.onerror = (e) => {
        console.error('Audio error:', e);
        // Fallback to oscillator if audio fails
        if (audioContext) {
          const osc = audioContext.createOscillator();
          const gain = audioContext.createGain();
          
          const freqMap = {
            drums: 100,
            bass: 60,
            synths: 440,
            loops: 220,
            fx: 300,
            vocals: 350,
            instruments: 261,
            world: 200,
          };
          
          osc.frequency.value = freqMap[sample.category] || 220;
          osc.type = 'sine';
          
          gain.gain.value = 0.2;
          gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + (sample.duration || 1));
          
          osc.connect(gain);
          gain.connect(audioContext.destination);
          
          osc.start();
          osc.stop(audioContext.currentTime + (sample.duration || 1));
          
          oscillatorRef.current = osc;
        }
        setTimeout(() => setPlayingSampleId(null), (sample.duration || 1) * 1000);
      };
      
      await audio.play();
      
    } catch (err) {
      console.error('Error playing sample:', err);
      setPlayingSampleId(null);
    }
  }, [audioContext]);

  const stopSample = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (oscillatorRef.current) {
      try { oscillatorRef.current.stop(); } catch (e) {}
    }
    setPlayingSampleId(null);
  }, []);

  // Add to favorites
  const addToFavorites = async (sample) => {
    try {
      if (favorites.includes(sample.id)) {
        await fetch(`${API_URL}/api/samples/favorites/${sample.id}`, { method: 'DELETE' });
        setFavorites(prev => prev.filter(id => id !== sample.id));
        toast.success('Removed from favorites');
      } else {
        await fetch(`${API_URL}/api/samples/favorites/${sample.id}`, { method: 'POST' });
        setFavorites(prev => [...prev, sample.id]);
        toast.success('Added to favorites');
      }
    } catch (err) {
      toast.error('Failed to update favorites');
    }
  };

  // Handle drag start for drag-to-track
  const handleDragStart = (e, sample) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'sample',
      sample: sample,
    }));
    e.dataTransfer.effectAllowed = 'copy';
  };

  // Insert sample into track
  const handleInsertSample = (sample) => {
    onInsertSample?.(sample);
    toast.success(`Added "${sample.name}" to track`);
  };

  if (!isOpen) return null;

  const currentCategory = categories.find(c => c.id === selectedCategory);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, x: -300 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -300 }}
        className="fixed left-0 top-0 bottom-0 w-80 bg-zinc-900 border-r border-zinc-800 z-40 flex flex-col shadow-2xl"
        data-testid="sample-library-browser"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Library className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-white">Sample Library</h2>
              <p className="text-xs text-zinc-400">
                {activeTab === 'builtin' ? `${samples.length} samples` : '500k+ sounds'}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-zinc-800">
          <button
            onClick={() => setActiveTab('builtin')}
            data-testid="sample-tab-builtin"
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-all ${
              activeTab === 'builtin'
                ? 'text-emerald-400 border-b-2 border-emerald-500 bg-emerald-500/10'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
            }`}
          >
            <Package className="w-4 h-4 inline mr-1.5" />
            Built-in
          </button>
          <button
            onClick={() => setActiveTab('freesound')}
            data-testid="sample-tab-freesound"
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-all ${
              activeTab === 'freesound'
                ? 'text-orange-400 border-b-2 border-orange-500 bg-orange-500/10'
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
            }`}
          >
            <Globe className="w-4 h-4 inline mr-1.5" />
            Freesound
          </button>
        </div>
        
        {/* Content based on active tab */}
        {activeTab === 'freesound' ? (
          <FreesoundBrowser onInsertSample={onInsertSample} />
        ) : (
        <>
        {/* Search */}
        <div className="p-3 border-b border-zinc-800">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search samples..."
              className="w-full pl-9 pr-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder:text-zinc-500 focus:border-emerald-500 focus:outline-none"
            />
          </div>
          
          {/* Quick filters */}
          <div className="flex gap-2 mt-2">
            <Button
              variant={showFavorites ? "default" : "outline"}
              size="sm"
              onClick={() => { setShowFavorites(!showFavorites); setSelectedCategory(null); }}
              className={`text-xs ${showFavorites ? 'bg-red-500 hover:bg-red-600' : 'border-zinc-700'}`}
            >
              <Heart className="w-3 h-3 mr-1" />
              Favorites
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="text-xs border-zinc-700"
            >
              <Filter className="w-3 h-3 mr-1" />
              Filters
            </Button>
          </div>
          
          {/* Extended filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="mt-2 pt-2 border-t border-zinc-800 space-y-2 overflow-hidden"
              >
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min BPM"
                    value={bpmFilter.min}
                    onChange={(e) => setBpmFilter(prev => ({ ...prev, min: e.target.value }))}
                    className="flex-1 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-xs text-white"
                  />
                  <input
                    type="number"
                    placeholder="Max BPM"
                    value={bpmFilter.max}
                    onChange={(e) => setBpmFilter(prev => ({ ...prev, max: e.target.value }))}
                    className="flex-1 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-xs text-white"
                  />
                </div>
                <select
                  value={keyFilter}
                  onChange={(e) => setKeyFilter(e.target.value)}
                  className="w-full px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-xs text-white"
                >
                  <option value="">Any Key</option>
                  {['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'].map(key => (
                    <option key={key} value={key}>{key}</option>
                  ))}
                </select>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* Categories */}
        {!showFavorites && !searchQuery && (
          <div className="p-3 border-b border-zinc-800 overflow-x-auto">
            <div className="flex gap-1">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  data-testid={`sample-cat-${cat.id}`}
                  onClick={() => {
                    setSelectedCategory(selectedCategory === cat.id ? null : cat.id);
                    setSelectedSubcategory(null);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-all ${
                    selectedCategory === cat.id
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50'
                      : 'bg-zinc-800 text-zinc-400 hover:text-white border border-zinc-700'
                  }`}
                >
                  <span className="mr-1">{CATEGORY_ICONS[cat.id] || '🎵'}</span>
                  {cat.name}
                </button>
              ))}
            </div>
            
            {/* Subcategories */}
            {currentCategory?.subcategories && (
              <div className="flex gap-1 mt-2 flex-wrap">
                {currentCategory.subcategories.map(sub => (
                  <button
                    key={sub}
                    onClick={() => setSelectedSubcategory(selectedSubcategory === sub ? null : sub)}
                    className={`px-2 py-1 rounded text-xs transition-all ${
                      selectedSubcategory === sub
                        ? 'bg-emerald-500 text-white'
                        : 'bg-zinc-800 text-zinc-400 hover:text-white'
                    }`}
                  >
                    {sub.replace('-', ' ')}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Sample List */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
            </div>
          ) : samples.length === 0 ? (
            <div className="text-center py-8">
              <Music2 className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
              <p className="text-sm text-zinc-500">
                {selectedCategory ? 'No samples found' : 'Select a category to browse'}
              </p>
            </div>
          ) : (
            samples.map(sample => (
              <SampleCard
                key={sample.id}
                sample={sample}
                isPlaying={playingSampleId === sample.id}
                onPlay={playSample}
                onStop={stopSample}
                onDragStart={handleDragStart}
                onAddToFavorites={addToFavorites}
                isFavorite={favorites.includes(sample.id)}
              />
            ))
          )}
        </div>
        
        {/* Footer */}
        <div className="p-3 border-t border-zinc-800">
          <p className="text-xs text-zinc-500 text-center">
            Drag samples to timeline to add
          </p>
        </div>
        </>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default SampleLibraryBrowser;
